import type { ReactNode } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Avatar } from '@systemeio/components'
import { useTranslation } from 'next-i18next'
import { twJoin } from 'tailwind-merge'
import { LikeButton } from 'shared/components/like/like-button'
import useUser from 'shared/hooks/use-user'
import EllipseIcon from 'shared/icons/ellipse-icon'
import type { AttachmentsRefInterface } from 'modules/attachments/components/context'
import { PostAttachmentsProvider } from 'modules/attachments/components/context'
import AttachmentsPreview from 'modules/attachments/components/preview'
import { useLikeComment } from 'modules/comments/api/like-comment'
import { useUnlikeComment } from 'modules/comments/api/unlike-comment'
import { useUpdateComment } from 'modules/comments/api/update-comment'
import CommentActions from 'modules/comments/components/comment-actions'
import { CommentEditor } from 'modules/comments/components/comment-editor'
import { TextWithMentions } from 'modules/comments/components/comment-editor/deserialize-mention-to-html'
import type { CommentInterface } from 'modules/comments/components/comment/comment.types'
import { CommentHeader } from 'modules/comments/components/comment/ui/comment-header'
import { useCommentsContext } from 'modules/comments/components/comments-list'
import { CommentListWithLoader } from 'modules/comments/components/comments-list/comment-list-with-loader'
import { MessageBox } from 'modules/comments/components/message-box/message-box'
import { useHighlighting } from 'modules/comments/hooks/use-highlighting'
import type { OnErrorType } from 'modules/comments/types'
import { EditorMode } from 'modules/comments/types'
import { isCommunityExtra } from 'modules/comments/utils/comments-extra-type-guards'
import {
  UnderModerationPlaceholder,
  shouldHideUnderModerationContent,
} from 'modules/community/components/under-moderation-placeholder'
import type { PostAttachmentType } from 'modules/community/types/post-attachment'
import type { CommentsSearchInterface } from 'modules/community/types/post-interface'
import { escapeAttachmentLocalFields } from 'modules/community/utils/attachments'

const LINE_HEIGHT_OFFSET = 25

export interface CommentProps {
  data: CommentInterface | CommentsSearchInterface
  level: 0 | 1
  activeComment?: CommentInterface | null
  setActiveComment?: (comment: CommentInterface | null) => void
  onError?: OnErrorType
  replies?: CommentInterface[] | null
  loadMoreReplies?: ({
    lastCommentId,
    parentId,
  }: {
    lastCommentId: number
    parentId: number
  }) => void
}

export const Comment = ({
  data,
  level,
  activeComment,
  setActiveComment,
  onError,
  replies,
  loadMoreReplies,
}: CommentProps): ReactNode => {
  const { t } = useTranslation()
  const { user } = useUser()
  const {
    setComments,
    commentsList,
    repliesList,
    updateReplies,
    readOnly,
    extra,
    setPinnedComments,
    pinnedComments,
  } = useCommentsContext()

  const [isRepliesOpen, setIsRepliesOpen] = useState(replies?.length || false)
  const [isEditing, setIsEditing] = useState(false)
  const [attachments, setAttachments] = useState<PostAttachmentType[]>([])
  const updateCommentMutation = useUpdateComment({ commentId: data.id })
  const likeCommentMutation = useLikeComment({ commentId: data.id })
  const unlikeCommentMutation = useUnlikeComment({ commentId: data.id })

  const attachmentsRef = useRef<AttachmentsRefInterface>(null)
  const lineRef = useRef<HTMLDivElement | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const commentRef = useRef<HTMLDivElement | null>(null)

  const isHighlighted = useHighlighting(data.id, commentRef)
  const isCommentOwner = data.canDeleteComment
  const commentTimestamp = new Date(data.updatedAt ?? data.createdAt).getTime()
  const wasEdited = data.updatedAt !== data.createdAt
  const isCommunityComments = isCommunityExtra(extra)
  const hideContent = shouldHideUnderModerationContent(data.underModeration, user)
  const interactionsDisabled = Boolean(readOnly) || hideContent

  const isSearchComment = 'highlight' in data

  const calculateLineHeight = useCallback(() => {
    if (containerRef.current && lineRef.current) {
      const container = containerRef.current
      const line = lineRef.current
      const containerHeight = container.scrollHeight

      const lastReplyElement = container.lastElementChild?.lastElementChild

      if (lastReplyElement instanceof HTMLElement) {
        const lastReplyHeight = lastReplyElement.offsetHeight
        const lineHeight = containerHeight - lastReplyHeight - LINE_HEIGHT_OFFSET
        line.style.height = `${lineHeight}px`
      }
    }
  }, [])

  const toggleReplies = (): void => {
    if (!isRepliesOpen && !replies?.length) {
      loadMoreReplies?.({ lastCommentId: 0, parentId: data.id })
    }
    setIsRepliesOpen(prev => !prev)
  }

  const handleReply = (): void => {
    commentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    setActiveComment?.(null)
    // NOTE: It's a workaround to have the comment field re-created due to React life-cycle. Since Slate is uncontrollable and we can only use initValue on mount component
    setTimeout(() => setActiveComment?.(data), 10)
    if (level === 0) {
      if (!replies?.length) {
        loadMoreReplies?.({ lastCommentId: 0, parentId: data.id })
      }
      setIsRepliesOpen(true)
    }
  }

  const handleLikeClick = (comment: CommentInterface) => async (): Promise<void> => {
    try {
      const { isLiked } = comment
      const delta = isLiked ? -1 : 1
      const updateFunction = isLiked ? unlikeCommentMutation.trigger : likeCommentMutation.trigger
      await updateFunction()

      const updateLikes = (commentData: CommentInterface): CommentInterface =>
        commentData.id === comment.id
          ? {
              ...commentData,
              likesCount: commentData.likesCount + delta,
              isLiked: !isLiked,
            }
          : commentData

      if (comment.isPinned) {
        setPinnedComments(pinnedComments.map(element => updateLikes(element)))
      } else {
        setComments(commentsList.map(element => updateLikes(element)))
      }

      if (level > 0 && !Array.isArray(data.parent)) {
        updateReplies({
          commentId: data.parent.id,
          replies: repliesList[data.parent.id]?.replies?.map(r =>
            r.id === comment.id
              ? { ...r, isLiked: !isLiked, likesCount: data.likesCount + delta }
              : r,
          ),
        })
      }
    } catch (error) {
      onError?.(error)
    }
  }

  const handleEditComment = async (
    value: string,
    attachments?: PostAttachmentType[],
  ): Promise<void> => {
    try {
      const updatedComment = await updateCommentMutation.trigger({
        text: value,
        attachments: escapeAttachmentLocalFields(attachments),
      })
      const optimisticCommentUpdate = { ...data, ...updatedComment }

      if (updatedComment.isPinned) {
        setPinnedComments(
          pinnedComments.map(comment =>
            comment.id === data.id ? optimisticCommentUpdate : comment,
          ),
        )
      } else {
        setComments(
          commentsList.map(comment => (comment.id === data.id ? optimisticCommentUpdate : comment)),
        )
      }

      if (level > 0 && !Array.isArray(data.parent)) {
        updateReplies({
          commentId: data.parent.id,
          replies: repliesList[data.parent.id]?.replies?.map(r =>
            r.id === data.id ? optimisticCommentUpdate : r,
          ),
        })
      }

      setIsEditing(false)
    } catch (error) {
      onError?.(error)
    }
  }

  const handleCancelEditing = useCallback(() => {
    setIsEditing(false)
  }, [])

  useEffect(() => {
    if (!replies?.length) return
    window.addEventListener('resize', calculateLineHeight)
    return () => {
      window.removeEventListener('resize', calculateLineHeight)
    }
  }, [calculateLineHeight, replies?.length])

  useEffect(() => {
    calculateLineHeight()
  }, [isRepliesOpen, calculateLineHeight, replies, data, isEditing])

  return (
    <PostAttachmentsProvider
      ref={attachmentsRef}
      attachments={hideContent ? [] : data.attachments}
      isEditable={isEditing && !hideContent}
      onChange={setAttachments}
    >
      {isEditing && !hideContent ? (
        <CommentEditor
          initialHtml={data.text}
          initialMentions={data.mentions}
          handleAddComment={handleEditComment}
          isRoot={false}
          mode={EditorMode.Edit}
          setActiveComment={setActiveComment ?? (() => {})}
          onCancel={handleCancelEditing}
          editableComment={data}
          className="mt-5"
        />
      ) : (
        <div className="relative" ref={containerRef} id={`comment-${data.id}`}>
          <div className="flex w-full flex-row items-start gap-2.5 sm:gap-2.5">
            {!!isRepliesOpen && (
              <div
                ref={lineRef}
                className="absolute top-[40px] left-[20px] w-px bg-text-secondary/30"
              ></div>
            )}
            <div className="relative">
              <Avatar
                alt={`Avatar ${data.userName}`}
                displayName={data.userName}
                rootProps={{ title: data.userName }}
                size="lg"
                src={data.userAvatarUrl || undefined}
              />
              {level > 0 && (
                <div className="absolute top-5 right-[39px] h-px w-[30px] bg-text-secondary/30"></div>
              )}
            </div>
            <div className="relative w-[calc(100%-40px)] min-w-0 grow-0 rounded-xl">
              <div
                className={twJoin(
                  'relative rounded-xl p-[15px] transition-colors duration-3000 ease-out',
                  data.id === activeComment?.id || isHighlighted
                    ? 'bg-bg-accent-active/20'
                    : 'bg-bg-page',
                )}
                ref={commentRef}
              >
                <div className="mb-3 flex items-center justify-between">
                  <CommentHeader
                    userName={data.userName}
                    userId={data.userId}
                    isCommunity={isCommunityComments}
                    commentTimestamp={commentTimestamp}
                    wasEdited={wasEdited}
                    isPinned={data?.isPinned ?? false}
                    highlightedAuthorHTML={isSearchComment ? data.highlight?.author : undefined}
                  />
                </div>

                {hideContent ? (
                  <UnderModerationPlaceholder type="comment" className="mt-1 md:mt-4" />
                ) : (
                  <>
                    <TextWithMentions
                      className="mt-1 text-start text-text-primary/70 [word-wrap:break-word] md:mt-4 [&_a]:text-text-link [&_a]:no-underline [&_a:hover]:underline [&_p]:m-0 [&_p]:p-0"
                      text={isSearchComment ? data.highlight.comment || data.text : data.text}
                      mentions={data.mentions}
                    />

                    <AttachmentsPreview className={twJoin(!!attachments?.length && 'pt-4')} />
                  </>
                )}

                {(isCommentOwner || isCommunityComments) && !hideContent && (
                  <CommentActions
                    commentData={data}
                    className="absolute top-3 right-3.5 justify-self-end"
                    onEdit={() => {
                      setIsEditing(true)
                    }}
                  />
                )}
              </div>
              <div className="mt-1.5 flex translate-y-1 items-center gap-2 text-sm">
                {!hideContent && (
                  <LikeButton
                    handleLikeClick={handleLikeClick(data)}
                    isLiked={data.isLiked}
                    likesCount={data.likesCount}
                    commentId={data.id}
                    readOnly={interactionsDisabled}
                  />
                )}
                {!interactionsDisabled && (
                  <div
                    className="cursor-pointer font-medium text-text-secondary hover:text-text-link"
                    onClick={handleReply}
                  >
                    {t('comments.components.comments.reply_label')}
                  </div>
                )}
                {data.totalReplies > 0 && !hideContent && <EllipseIcon />}
                {level === 0 && !!data.totalReplies && (
                  <div className="mr-2 flex cursor-pointer text-text-link" onClick={toggleReplies}>
                    {isRepliesOpen
                      ? t('comments.components.comments.responses_label_hide')
                      : `${data.totalReplies} ${t(
                          data.totalReplies === 1
                            ? 'comments.components.comments.responses_label_one'
                            : 'comments.components.comments.responses_label_other',
                        )}`}
                  </div>
                )}
              </div>
              {!isCommunityComments && !interactionsDisabled && activeComment?.id === data.id && (
                <MessageBox
                  parentId={Array.isArray(data.parent) ? data.id : data.parent.id}
                  setActiveComment={setActiveComment}
                  level={1}
                  onError={onError}
                  setResponses={(_, reply) => {
                    const parentId = Array.isArray(data.parent) ? data.id : data.parent.id
                    updateReplies({
                      commentId: parentId,
                      replies: [reply, ...(repliesList[parentId]?.replies ?? [])],
                    })
                  }}
                />
              )}
            </div>
          </div>

          {!!isRepliesOpen && (
            <div className="mt-5 ml-[50px] flex flex-col space-y-5 md:mt-6 md:space-y-6">
              <CommentListWithLoader
                items={replies ?? []}
                renderItem={reply => (
                  <Comment
                    key={reply.id}
                    data={reply}
                    level={1}
                    setActiveComment={setActiveComment}
                    activeComment={activeComment}
                    onError={onError}
                  />
                )}
                onLoadMore={({ lastCommentId }) =>
                  loadMoreReplies?.({ lastCommentId, parentId: data.id })
                }
                parentId={data.id}
                loadMoreLabel={t('comments.components.comments.load_more_label')}
              />
            </div>
          )}
        </div>
      )}
    </PostAttachmentsProvider>
  )
}
